// src/pages/BrokerSignup.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth, createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { getFirestore, doc, setDoc, serverTimestamp } from "firebase/firestore";
import app from "../firebase";
import { Helmet } from "react-helmet-async";
import { useToast } from "../context/ToastContext";

const auth = getAuth(app);
const db = getFirestore(app);

const getFriendlyFirebaseError = (code) => {
  const map = {
    "auth/email-already-in-use": "An account with this email already exists.",
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/weak-password": "Password should be at least 6 characters.",
  };
  return map[code] || "An unexpected error occurred. Please try again.";
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(email || ""));

export default function BrokerSignup() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your full name.";
    if (!isValidEmail(form.email)) return "Please enter a valid email address.";
    if (!form.phone.trim()) return "Please enter a phone number.";
    if (form.password.length < 6) return "Password should be at least 6 characters.";
    if (form.password !== form.confirmPassword) return "Passwords do not match.";
    return "";
  };

  const handleSignup = async () => {
    const validationError = validate();
    if (validationError) {
      addToast(validationError, "warning");
      return;
    }

    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await updateProfile(cred.user, { displayName: form.name });

      // Mirror the users/{uid} record that AdminSignup creates, so every
      // authenticated account (admin or broker) shows up in `users` with a role.
      await setDoc(doc(db, "users", cred.user.uid), {
        email: form.email.trim(),
        name: form.name.trim(),
        createdAt: serverTimestamp(),
        role: "Broker Applicant", // promoted to "Broker" only on admin approval
      });

      await setDoc(doc(db, "broker_applications", cred.user.uid), {
        uid: cred.user.uid,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        status: "pending", // pending | approved | rejected
        createdAt: serverTimestamp(),
      });

      addToast("Application submitted!", "success");
      setSubmitted(true);
    } catch (error) {
      addToast(getFriendlyFirebaseError(error.code), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Your Choice Associates | Become a Broker</title>
        <meta name="description" content="Apply to become a broker with Your Choice Associates." />
        <link rel="canonical" href="https://yourchoiceassociates.org/broker-signup" />
      </Helmet>

      {/* Topbar */}
      <div className="w-full bg-[#002b5c] text-white py-4 px-6 flex items-center justify-between shadow-md">
        <img src="/images/logo.png" alt="Logo" className="h-10" />
        <span className="text-sm opacity-80">Broker Signup</span>
      </div>

      {/* Main Content */}
      <div className="flex flex-1">
        <div className="w-full md:w-1/2 flex flex-col justify-center px-10 md:px-20 bg-white">
          <div className="mt-12 md:mt-0">
            {submitted ? (
              <div>
                <h2 className="text-3xl font-extrabold text-[#002b5c] mb-3">Application submitted</h2>
                <p className="text-sm text-gray-600 mb-8">
                  Thanks for applying to become a broker. Your application is pending review —
                  we'll notify you at <strong>{form.email}</strong> once it's been approved.
                </p>
                <Link
                  to="/broker-login"
                  className="inline-block bg-[#002b5c] text-white py-3 px-6 rounded-lg hover:bg-[#013a76] transition font-semibold shadow-md"
                >
                  Go to broker login
                </Link>
              </div>
            ) : (
              <>
                <h2 className="text-3xl font-extrabold text-[#002b5c] mb-3">Become a Broker</h2>
                <p className="text-sm text-gray-500 mb-8">
                  Submit your details below. An admin will review and approve your account.
                </p>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Full Name</label>
                    <input
                      name="name"
                      placeholder="Jane Doe"
                      value={form.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002b5c]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002b5c]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Phone</label>
                    <input
                      name="phone"
                      placeholder="(555) 555-5555"
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002b5c]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Password</label>
                    <input
                      type="password"
                      name="password"
                      placeholder="••••••••"
                      value={form.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002b5c]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      placeholder="••••••••"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002b5c]"
                    />
                  </div>

                  <button
                    onClick={handleSignup}
                    disabled={loading}
                    className="w-full bg-[#ef3b51] text-white py-3 rounded-lg hover:bg-[#d72f44] transition font-semibold shadow-md disabled:opacity-60"
                  >
                    {loading ? "Submitting..." : "Submit Application"}
                  </button>

                  <p className="text-sm text-center text-gray-600 pt-2">
                    Already have an account?{" "}
                    <Link to="/broker-login" className="text-[#002b5c] font-medium hover:underline">
                      Log in here
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="hidden md:block md:w-1/2 h-screen">
          <img src="/images/hero.png" alt="Hero Visual" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  );
}
